<template>
  <div class="current_keywords">
    <div
      v-if="combinedChosenKeywords.length > 0"
      class="table_wrapper"
    >
      <table
        class="keywords_table head"
        :class="{'can-edit': canEditKeywords}"
      >
        <thead>
          <tr>
            <td class="keyword">
              <div
                class="sort_title"
                @click="setSort('keyword')"
              >
                <span>Keyword</span>
                <i
                  class="icon"
                  :class="{
                    arrow_down: sort === 'keyword' && sortDirection === 'desc',
                    arrow_up: sort !== 'keyword' || (sort === 'keyword' && sortDirection === 'asc'),
                  }"
                />
              </div>
            </td>
            <td>
              <div
                v-tooltip="'Search volume is a measure of the average number of searches that are performed through Google per month'"
                class="sort_title center"
                @click="setSort('search_volume')"
              >
                <span>Search Volume</span>
                <i
                  class="icon"
                  :class="{
                    arrow_down: sort === 'search_volume' && sortDirection === 'desc',
                    arrow_up: sort !== 'search_volume' || (sort === 'search_volume' && sortDirection === 'asc'),
                  }"
                />
              </div>
            </td>
            <td>
              <div
                v-tooltip="difficultyTooltip"
                class="sort_title center"
                @click="setSort('difficulty')"
              >
                <span>Difficulty</span>
                <i
                  class="icon"
                  :class="{
                    arrow_down: sort === 'difficulty' && sortDirection === 'desc',
                    arrow_up: sort !== 'difficulty' || (sort === 'difficulty' && sortDirection === 'asc'),
                  }"
                />
              </div>
            </td>
            <td v-if="!isSubscribedToKit">
              <div
                v-tooltip="'Receive feedback on each keyword, determining if we think it\'s a highly relevant keyword for your selected industry. Use this to help guide you in making your keyword choices.'"
              >
                <span>Review</span>
              </div>
            </td>
            <td v-if="canEditKeywords">
              &nbsp;
            </td>
          </tr>
        </thead>
      </table>
      <div class="table_body_wrap">
        <table
          class="keywords_table"
          :class="{'can-edit': canEditKeywords}"
        >
          <tbody>
            <tr
              v-for="keyword in combinedChosenKeywords"
              :key="keyword.id"
            >
              <td class="keyword p_small">
                <span
                  v-if="keyword.new || 0"
                  class="new"
                >
                  New
                </span>
                {{ keyword.keyword }}
                <i
                  v-if="keyword.assigned_page"
                  v-tooltip="{
                    content: `This keyword is assigned to the page - <strong>${ getURLPath(keyword.assigned_page) }</strong>`,
                    html: true,
                  }"
                  class="far fa-sitemap xs-ml-5"
                />
              </td>
              <td class="volume">
                {{ keyword.search_volume > 0 ? keyword.search_volume.toLocaleString() : '< 10' }}
              </td>
              <td class="difficulty">
                <difficulty-tag
                  :difficulty="
                    keyword.difficulty !== 999
                      ? keyword.difficulty
                      : keyword.calculated_difficulty
                  "
                />
              </td>
              <td v-if="!isSubscribedToKit">
                <keyword-review-status
                  v-if="displayReview(keyword)"
                  :keyword-review="getKeywordReview(keyword)"
                  :hide-missing="true"
                  pending-message="Loading Review..."
                />
              </td>
              <td v-if="canEditKeywords">
                <button
                  class="remove_btn red_btn"
                  @click="removeKeyword(keyword)"
                >
                  <svg
                    fill="none"
                    height="14"
                    viewBox="0 0 14 14"
                    width="14"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2.1875 7H11.8125"
                      stroke="white"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="1.5"
                    />
                  </svg>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div
      v-else
      class="no_results"
    >
      No currently saved keywords
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import _ from "lodash";
import DifficultyTag from "../../../../../../../js/components/global/assets/DifficultyTag.vue";
import KeywordReviewStatus from "../components/KeywordReviewStatus.vue";
import useKit from "../../../../../../../js/composables/useKit";

export default {
  components: {
    KeywordReviewStatus,
    DifficultyTag,
  },
  setup() {
    const { isSubscribedToKit } = useKit();
    return { isSubscribedToKit };
  },
  data() {
    return {
      reviewEnabled: false,
      sort: 'difficulty',
      sortDirection: 'desc',
      difficultyTooltip: 'Keyword difficulty is a metric indicating how difficult it is to rank in the '+
        'top 10 search results for a keyword. Try to pick keywords with a lower difficulty value where '+
        'possible, as it should be easier to rank in Google.'
    }
  },
  computed: {
    ...mapGetters('keywordIdeas', [
      'chosenKeywords',
      'newChosenKeywords',
      'existingReviews',
      'loadingExistingReviews',
    ]),
    ...mapGetters('user', [
      'canEditKeywords'
    ]),
    combinedChosenKeywords() {
      let newKeywords = [];
      this.newChosenKeywords.forEach(keyword => {
        keyword.new = 1;
        newKeywords.push(keyword);
      });
      this.chosenKeywords.forEach(keyword => {
        keyword.new = 0;
        newKeywords.push(keyword);
      });

      newKeywords = _.orderBy(newKeywords, ['new', this.sort], ['desc', this.sortDirection]);

      return newKeywords;
    },
  },
  methods: {
    ...mapActions('keywordIdeas', [
      'removeNewChosenKeyword',
      'removeChosenKeyword',
    ]),
    getKeywordReview(keyword) {
      return this.existingReviews?.find(({keyword_index_id}) => keyword.keyword_index_id === keyword_index_id);
    },
    removeKeyword(keyword) {
      if (keyword.new || 0) {
        let index = this.newChosenKeywords.findIndex((newChosenKeyword) => newChosenKeyword.keyword === keyword.keyword);
        this.removeNewChosenKeyword(index);
      } else {
        let index = this.chosenKeywords.findIndex((chosenKeyword) => chosenKeyword.keyword === keyword.keyword);
        this.removeChosenKeyword(index);
      }
    },
    setSort(sortBy) {
      if (this.sort === sortBy) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sort = sortBy;
        switch (sortBy) {
          case 'keyword':
            this.sortDirection = 'asc';
            break;
          case 'search_volume':
            this.sortDirection = 'desc';
            break;
          case 'difficulty':
            this.sortDirection = 'desc';
            break;
        }
      }
    },
    getURLPath(url) {
      try {
        let urlObject = new URL(url);
        return urlObject.pathname;
      } catch (e) {
        return url;
      }
    },
    displayReview(keyword) {
      if (keyword?.remove || keyword?.new) {
        return false;
      }

      return this.loadingExistingReviews === true || this.getKeywordReview(keyword)?.decision !== undefined;
    }
  }
}
</script>

<style lang="scss" scoped>
.table_wrapper {
  margin-bottom: 10px;

  .table_body_wrap {
    overflow-y: scroll;

    @media screen and (max-height: 1000px) {
      height: 450px;
    }

    @media screen and (min-height: 1001px) {
      height: 500px;
    }
  }
}

.keywords_table {
  width: 100%;
  border-spacing: 0;
  border-collapse: collapse;

  &.can-edit {
    tr td {
      &:nth-of-type(2) {
        width: 20%;
        text-align: center;
      }

      &:nth-of-type(3) {
        width: 20%;
        text-align: center;
      }

      &:nth-of-type(4) {
        width: 10%;
        text-align: center;
      }

      &:nth-of-type(5) {
        width: 10%;
        text-align: center;
      }
    }
  }

  .sort_title {
    cursor: pointer;
    display: flex;
    align-items: center;

    &.center {
      justify-content: center;
    }

    .arrow_down {
      background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8 2.5V13.5' stroke='%23040707' stroke-width='0.875' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M3.5 9L8 13.5L12.5 9' stroke='%23040707' stroke-width='0.875' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
    }

    .arrow_up {
      background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8 2.5V13.5' stroke='%23040707' stroke-width='0.875' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M3.5 9L8 13.5L12.5 9' stroke='%23040707' stroke-width='0.875' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
      transform: rotate(180deg);
    }
  }

  thead td {
    span {
      font-weight: 500;
      font-size: 16px;
      line-height: 26px;
      letter-spacing: -0.25px;
      margin-right: 0.25rem;
    }
  }

  tbody tr td {
    .new,
    .remove {
      margin-right: 15px;
      padding: 0.25rem 0.5rem;
      font-size: 10px;
      line-height: 1.3;
      letter-spacing: -0.1px;
      border-radius: 6px;
    }

    .new {
      background-color: #DFF5EE;
      color: #37B2A8;
    }

    .remove {
      background-color: var(--difficulty-very-high-color);
      color: var(--difficulty-very-high-font-color);
    }
  }

  tr td {
    padding: 1rem;

    &.keyword {
      display: flex;
      justify-content: flex-start;
      flex-direction: row;
      align-items: center;
      width: 100%;
    }

    &:nth-of-type(2) {
      width: 25%;
      text-align: center;
    }

    &:nth-of-type(3) {
      width: 25%;
      text-align: center;
    }

    &:nth-of-type(4) {
      width: 10%;
      text-align: center;
    }
  }
}

.add_button {
  width: 30px;
  height: 30px;
  cursor: pointer;
  border-radius: 6px;
  border: 2px solid #37B2A8;
  background-image: url("data:image/svg+xml,%3Csvg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M2.1875 7H11.8125' stroke='%2337B2A8' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M7 2.1875V11.8125' stroke='%2337B2A8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
  background-repeat: no-repeat;
  background-position: center;
}

.remove_btn {
  border-radius: 8px;
  width: 32px;
  height: 32px;
  background-color: #F34F4F !important;
  color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
}

.no_results {
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  margin-bottom: 10px;
  font-size: 1.75em;
  font-style: normal;
  height: 100px;
}
</style>
